/**
 * Single source of truth for the tarot reading spreads.
 *
 * Each spread used to be its own near-identical screen + route (~32 files of
 * boilerplate that differed only by reading-type). They're now one data entry
 * each, consumed by `ReadingScreen.vue` (the one screen) and `router.ts` (which
 * derives the `/reading/:slug` routes from this map). Add a spread = add a line.
 */

/** Backend SpreadType — the reading types the app draws for (mirrors ReadingFactory). */
export type ReadingType =
  | 'DAILY'
  | 'SINGLE_CARD'
  | 'THREE_CARD'
  | 'YES_NO'
  | 'LOVE'
  | 'A_OR_B'
  | 'FINANCIAL'
  | 'DESTINY'
  | 'EDUCATION'
  | 'EX_PARTNER'
  | 'DISAPPEARANCE'
  | 'WAIT_OR_RELEASE'
  | 'SITUATION'
  | 'CAREER_CROSSROADS'
  | 'TALENT'
  | 'SELF_DISCOVERY';

export interface SpreadConfig {
  /** SpreadType passed to the draw + reading. */
  type: ReadingType;
  /**
   * Optional pre-draw step:
   *  - 'question' → free-text `QuestionStep` (parameterised by reading-type)
   *  - 'choice'   → A/B `QuestionOrStep`
   * Omit for a spread that draws immediately.
   */
  step?: 'question' | 'choice';
}

export const SPREADS = {
  daily: { type: 'DAILY' },
  'question-single': { type: 'SINGLE_CARD', step: 'question' },
  'question-three': { type: 'THREE_CARD', step: 'question' },
  'yes-no': { type: 'YES_NO', step: 'question' },
  love: { type: 'LOVE' },
  'a-or-b': { type: 'A_OR_B', step: 'choice' },
  financial: { type: 'FINANCIAL' },
  destiny: { type: 'DESTINY' },
  education: { type: 'EDUCATION' },
  'ex-partner': { type: 'EX_PARTNER' },
  disappearance: { type: 'DISAPPEARANCE' },
  'wait-or-release': { type: 'WAIT_OR_RELEASE' },
  situation: { type: 'SITUATION', step: 'question' },
  'career-crossroads': { type: 'CAREER_CROSSROADS' },
  talent: { type: 'TALENT' },
  'self-discovery': { type: 'SELF_DISCOVERY' },
} as const satisfies Record<string, SpreadConfig>;

export type SpreadSlug = keyof typeof SPREADS;

/** URL slugs, in declaration order — used to derive the reading routes. */
export const SPREAD_SLUGS = Object.keys(SPREADS) as SpreadSlug[];
